// crypto.ts - Secure Messaging Module using Libsodium
import sodium from 'libsodium-wrappers';

/**
 * Interfaces
 */
export interface KeyPair {
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}

export interface EncryptedMessage {
  ciphertext: string;
  nonce: string;
  senderPublicKey?: string;
   version?: string;
  timestamp?: string;
  //senderPublicKey: string;
}

export interface DecryptionParams {
  ciphertext: string;
  nonce: string;
}

/**
 * Initialize sodium (call this once at app startup)
 */
export async function initializeCrypto(): Promise<void> {
  await sodium.ready;
  console.log('Libsodium is ready');
}

/**
 * Key Generation
 */
export async function generateKeyPair(): Promise<KeyPair> {
  try {
    await sodium.ready;
    const keypair = sodium.crypto_box_keypair();
    return {
      publicKey: keypair.publicKey,
      privateKey: keypair.privateKey
    };
  } catch (error) {
    throw new Error(`Key generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Message Encryption
 */
// export async function encryptMessage(
//   recipientPublicKey: Uint8Array,
//   message: string,
//   senderPrivateKey: Uint8Array,
// ): Promise<EncryptedMessage> {
//   try {
//     await sodium.ready;

//     // Validate inputs
//     if (!recipientPublicKey || recipientPublicKey.length !== sodium.crypto_box_PUBLICKEYBYTES) {
//       throw new Error('Invalid recipient public key');
//     }

   
//     const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
    
//     const ciphertext = sodium.crypto_box_easy(
//       message,
//       nonce,
//       recipientPublicKey,
//       senderPrivateKey
//     );

//     return {
//       ciphertext: sodium.to_base64(ciphertext),
//       nonce: sodium.to_base64(nonce)//,
//       //senderPublicKey: sodium.to_base64(keyPair.publicKey)
//     };
//   } catch (error) {
//     throw new Error(`Encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
//   }
// }

export async function encryptMessage(
  recipientPublicKey: Uint8Array,
  message: string,
  senderPrivateKey: Uint8Array,
  senderPublicKey?: Uint8Array // Optional for verification
): Promise<EncryptedMessage> {
  try {
    await sodium.ready;

    // Validate inputs more thoroughly
    if (!recipientPublicKey || recipientPublicKey.length !== sodium.crypto_box_PUBLICKEYBYTES) {
      throw new Error(`Invalid recipient public key (expected ${sodium.crypto_box_PUBLICKEYBYTES} bytes)`);
    }

    if (!senderPrivateKey || senderPrivateKey.length !== sodium.crypto_box_SECRETKEYBYTES) {
      throw new Error(`Invalid sender private key (expected ${sodium.crypto_box_SECRETKEYBYTES} bytes)`);
    }

    // Convert message to Uint8Array if it's not already
    const messageBytes = typeof message === 'string' 
      ? sodium.from_string(message) 
      : message;

    // Generate random nonce
    const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
    
    // Perform encryption
    const ciphertext = sodium.crypto_box_easy(
      messageBytes,
      nonce,
      recipientPublicKey,
      senderPrivateKey
    );

    // Include additional security metadata
    return {
      ciphertext: sodium.to_base64(ciphertext),
      nonce: sodium.to_base64(nonce),
      senderPublicKey: senderPublicKey ? sodium.to_base64(senderPublicKey) : undefined,
      version: 'x25519-xsalsa20-poly1305', // Explicit algorithm identifier
      timestamp: new Date().toISOString() // For replay protection
    };
  } catch (error) {
    console.error('Encryption error details:', {
      recipientKey: recipientPublicKey?.length,
      privateKey: senderPrivateKey?.length,
      messageLength: message?.length
    });
    throw new Error(`Encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Message Decryption
 */
// export async function decryptMessage(
//   senderPublicKey: Uint8Array,
//   privateKey: Uint8Array,
//   encryptedData: DecryptionParams
// ): Promise<string> {
//   try {
//     await sodium.ready;

//     // Validate inputs
//     if (!senderPublicKey || senderPublicKey.length !== sodium.crypto_box_PUBLICKEYBYTES) {
//       throw new Error('Invalid sender public key');
//     }
//     if (!privateKey || privateKey.length !== sodium.crypto_box_SECRETKEYBYTES) {
//       throw new Error('Invalid private key');
//     }

//     const decryptedMessage = sodium.crypto_box_open_easy(
//       sodium.from_base64(encryptedData.ciphertext),
//       sodium.from_base64(encryptedData.nonce),
//       senderPublicKey,
//       privateKey
//     );

//     return new TextDecoder().decode(decryptedMessage);
//   } catch (error) {
//     throw new Error(`Decryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
//   }
// }
export async function decryptMessage(
  senderPublicKey: Uint8Array,
  privateKey: Uint8Array,
  encryptedData: EncryptedMessage
): Promise<string> {
  try {
    await sodium.ready;

    // Validate inputs
    if (!senderPublicKey || senderPublicKey.length !== sodium.crypto_box_PUBLICKEYBYTES) {
      throw new Error(`Invalid sender public key (expected ${sodium.crypto_box_PUBLICKEYBYTES} bytes, got ${senderPublicKey?.length})`);
    }

    if (!privateKey || privateKey.length !== sodium.crypto_box_SECRETKEYBYTES) {
      throw new Error(`Invalid private key (expected ${sodium.crypto_box_SECRETKEYBYTES} bytes, got ${privateKey?.length})`);
    }

    if (!encryptedData?.ciphertext || !encryptedData?.nonce) {
      throw new Error('Missing required encryption parameters');
    }

    // Convert base64 strings to Uint8Arrays
    let ciphertext: Uint8Array;
    let nonce: Uint8Array;
    
    try {
      ciphertext = sodium.from_base64(encryptedData.ciphertext);
      nonce = sodium.from_base64(encryptedData.nonce);
    } catch (e) {
      throw new Error('Invalid base64 encoding in ciphertext or nonce');
    }

    // Verify nonce length
    if (nonce.length !== sodium.crypto_box_NONCEBYTES) {
      throw new Error(`Invalid nonce length (expected ${sodium.crypto_box_NONCEBYTES} bytes)`);
    }

    // Optional: Verify sender public key matches if provided in encryptedData
    if (encryptedData.senderPublicKey) {
      const providedSenderKey = sodium.from_base64(encryptedData.senderPublicKey);
      if (!sodium.memcmp(providedSenderKey, senderPublicKey)) {
        throw new Error('Sender public key mismatch');
      }
    }

    // Perform decryption
    const decryptedMessage = sodium.crypto_box_open_easy(
      ciphertext,
      nonce,
      senderPublicKey,
      privateKey
    );

    if (!decryptedMessage) {
      throw new Error('Decryption failed - invalid ciphertext or keys');
    }

    // Convert to string and validate output
    const decodedMessage = new TextDecoder().decode(decryptedMessage);
    
    if (!decodedMessage || decodedMessage.trim().length === 0) {
      throw new Error('Decrypted message is empty or invalid');
    }

    return decodedMessage;
  } catch (error) {
    console.error('Decryption failed with details:', {
      error: error instanceof Error ? error.message : error,
      senderKeyLength: senderPublicKey?.length,
      privateKeyLength: privateKey?.length,
      ciphertextLength: encryptedData?.ciphertext?.length,
      nonceLength: encryptedData?.nonce?.length
    });
    
    throw new Error(`Decryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export function ensureUint8Array(key: string | undefined, name: string): Uint8Array {
  if (!key) throw new Error(`Missing ${name}`);
  try {
    return sodium.from_base64(key);
  } catch (e) {
    throw new Error(`Invalid ${name} format`);
  }
}

export function base64ToUint8Array(base64String: string): Uint8Array {
    // Remove potential data URL prefix and whitespace
    const base64 = base64String.split(',')[1]?.trim() || base64String.trim();
    
    // Convert Base64 to binary string
    const binaryString = atob(base64);
    
    // Create Uint8Array
    const uint8Array = new Uint8Array(binaryString.length);
    
    // Fill the array with byte values
    for (let i = 0; i < binaryString.length; i++) {
        uint8Array[i] = binaryString.charCodeAt(i);
    }
    
    return uint8Array;
}

export async function csPublicKeyToSodium(base64Key: string): Promise<Uint8Array> {
    await sodium.ready;
    
    // C# base64 may include padding that needs normalization
    const normalized = base64Key
        .trim()
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .replace(/\s+/g, '');

    try {
        const keyBytes = sodium.from_base64(normalized);
        
        // Validate key length (32 bytes for crypto_box)
        if (keyBytes.length !== sodium.crypto_box_PUBLICKEYBYTES) {
            throw new Error(`Invalid public key length. Expected ${sodium.crypto_box_PUBLICKEYBYTES} bytes, got ${keyBytes.length}`);
        }
        
        return keyBytes;
    } catch (e) {
        throw new Error(`Failed to convert public key: ${e instanceof Error ? e.message : 'Unknown error'}`);
    }
}

export async function encryptForCs(
    message: string,
    recipientPublicKey: string, // Base64 from C#
    senderPrivateKey: Uint8Array // From your TypeScript key pair
): Promise<{
    ciphertext: string;
    nonce: string;
}> {
    await sodium.ready;

    try {
        // Convert C# public key
        const recipientKey = await csPublicKeyToSodium(recipientPublicKey);
        
        // Generate nonce (24 bytes for crypto_box)
        const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
        
        // Encrypt the message
        const ciphertext = sodium.crypto_box_easy(
            sodium.from_string(message),
            nonce,
            recipientKey,
            senderPrivateKey
        );
        
        return {
            ciphertext: sodium.to_base64(ciphertext),
            nonce: sodium.to_base64(nonce)
        };
    } catch (error) {
        console.error('Encryption error details:', {
            recipientKey: recipientPublicKey?.length,
            messageLength: message?.length
        });
        throw new Error(`Encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}


/**
 * Key Conversion Utilities
 */
export function publicKeyToBase64(publicKey: Uint8Array): string {
  return sodium.to_base64(publicKey);
}

export function base64ToPublicKey(base64Key: string): Uint8Array {
  return sodium.from_base64(base64Key);
}

export function privateKeyToBase64(privateKey: Uint8Array): string {
  return sodium.to_base64(privateKey);
}

export function base64ToPrivateKey(base64Key: string): Uint8Array {
  return sodium.from_base64(base64Key);
}

/**
 * Key Validation
 */
export function isValidPublicKey(key: Uint8Array): boolean {
  return key && key.length === sodium.crypto_box_PUBLICKEYBYTES;
}

export function isValidPrivateKey(key: Uint8Array): boolean {
  return key && key.length === sodium.crypto_box_SECRETKEYBYTES;
}